/**
 * SubscriptionsPage - Admin page for monitoring services and subscriptions
 *
 * Two sections: Automatic (live data) and Manual (user-edited).
 * Services with active alerts sort to the top.
 */

import { useEffect, useState } from "react";
import { Button, Typography, Space, Skeleton, Divider, message } from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import {
  AUTO_SERVICES,
  subscribeToSnapshots,
  subscribeToManualServices,
  refreshSnapshots,
  seedManualServices,
} from "@/services/subscriptionsService";
import type {
  ServiceSnapshot,
  ManualService,
} from "@/services/subscriptionsService";
import AutoServiceCard from "./components/AutoServiceCard";
import ManualServiceCard from "./components/ManualServiceCard";
import EditServiceModal from "./components/EditServiceModal";

const { Title, Text } = Typography;

export default function SubscriptionsPage() {
  const [snapshots, setSnapshots] = useState<Record<string, ServiceSnapshot>>({});
  const [manualServices, setManualServices] = useState<ManualService[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editingService, setEditingService] = useState<ManualService | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let snapshotsReady = false;
    let servicesReady = false;

    const checkReady = () => {
      if (snapshotsReady && servicesReady) setLoading(false);
    };

    const handleError = (err: Error) => {
      setError(err.message);
      setLoading(false);
    };

    const unsubSnapshots = subscribeToSnapshots(
      (data) => {
        setSnapshots(data);
        snapshotsReady = true;
        checkReady();
      },
      handleError
    );

    const unsubServices = subscribeToManualServices(
      (data) => {
        setManualServices(data);
        servicesReady = true;
        checkReady();
      },
      handleError
    );

    return () => {
      unsubSnapshots();
      unsubServices();
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshSnapshots();
      message.success("Snapshots refreshed");
    } catch (error) {
      console.error("Refresh failed:", error);
      message.error("Failed to refresh snapshots");
    } finally {
      setRefreshing(false);
    }
  };

  // Sort manual services: those with alerts first
  const sortedManualServices = [...manualServices].sort((a, b) => {
    const aAlert = a.expirationDate ? 1 : 0;
    const bAlert = b.expirationDate ? 1 : 0;
    return bAlert - aAlert;
  });

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedManualServices();
      message.success("Manual services seeded");
    } catch (err) {
      console.error("Seed failed:", err);
      message.error("Failed to seed manual services");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return <Skeleton active title={false} paragraph={{ rows: 8 }} />;
  }

  if (error) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Text type="danger">Failed to load subscriptions: {error}</Text>
      </div>
    );
  }

  const refreshButton = (
    <Button
      icon={<MaterialIcon name="refresh" />}
      onClick={handleRefresh}
      loading={refreshing}
    >
      Refresh All
    </Button>
  );

  return (
    <div>
      <Title level={3}>Subscriptions</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Monitor all project services, quotas, and subscriptions.
      </Text>

      {/* Automatic services */}
      <Space style={{ marginBottom: 16 }} align="center">
        <Title level={4} style={{ margin: 0 }}>Live Services</Title>
        {refreshButton}
      </Space>

      {AUTO_SERVICES.map((svc) => (
        <AutoServiceCard
          key={svc.id}
          serviceId={svc.id}
          title={svc.name}
          deepLink={svc.deepLink}
          snapshot={snapshots[svc.id] || null}
        />
      ))}

      <Divider />

      {/* Manual services */}
      <Title level={4}>Manual Services</Title>

      {sortedManualServices.length === 0 ? (
        <Space direction="vertical" align="center" style={{ width: "100%", padding: 24 }}>
          <Text type="secondary">No manual services configured yet.</Text>
          <Button
            type="primary"
            icon={<MaterialIcon name="add_circle" />}
            onClick={handleSeed}
            loading={seeding}
          >
            Seed Default Services
          </Button>
        </Space>
      ) : (
        sortedManualServices.map((svc) => (
          <ManualServiceCard
            key={svc.id}
            service={svc}
            onEdit={setEditingService}
          />
        ))
      )}

      <EditServiceModal
        service={editingService}
        open={editingService !== null}
        onClose={() => setEditingService(null)}
      />
    </div>
  );
}
